import * as Sentry from '@sentry/react';

export const initSentry = () => {
  // Initialize in production only
  if (import.meta.env.PROD) {
    const dsn = import.meta.env.VITE_SENTRY_DSN;
    
    if (!dsn) {
      console.warn('Sentry DSN not configured. Error tracking disabled.');
      return;
    }

    Sentry.init({
      dsn,
      integrations: [
        Sentry.browserTracingIntegration({
          tracePropagationTargets: ['localhost', /^https:\/\/.*\.netlify\.app/, /^https:\/\/.*\.supabase\.co/],
        }),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      
      // Performance Monitoring
      tracesSampleRate: 0.2, // 20% of transactions for performance monitoring
      
      // Session Replay
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
      
      // Environment
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_APP_VERSION || '1.0.0',
      
      // Filter before sending
      beforeSend(event, hint) {
        // Filter out non-critical errors
        if (event.exception) {
          const error = hint.originalException;
          const errorMessage = error instanceof Error ? error.message : String(error);
          
          // Don't send network errors that are user-related
          if (errorMessage.includes('Failed to fetch') || errorMessage.includes('Network request failed')) {
            return null;
          }
          
          // Don't send cancelled requests
          if (errorMessage.includes('AbortError') || errorMessage.includes('Request aborted')) {
            return null;
          }
        }
        return event;
      },
      
      // Tag all events
      initialScope: {
        tags: {
          deployment: 'netlify',
          framework: 'react-vite',
        },
      },
    });
  }
};

// Enhanced error capture with context
export const captureException = (
  error: Error, 
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
    level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
  }
) => {
  if (import.meta.env.PROD) {
    Sentry.captureException(error, {
      level: context?.level || 'error',
      tags: context?.tags,
      extra: context?.extra,
    });
  } else {
    // Enhanced dev logging
    const errorLevel = context?.level || 'error';
    console.group(`[${errorLevel.toUpperCase()}] ${error.name}`);
    console.error('Message:', error.message);
    if (error.stack) console.error('Stack:', error.stack);
    if (context?.tags) console.log('Tags:', context.tags);
    if (context?.extra) console.log('Extra:', context.extra);
    console.groupEnd();
  }
};

// Breadcrumb tracking for user actions
export const addBreadcrumb = (
  message: string,
  category: string,
  data?: Record<string, any>,
  level: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug' = 'info'
) => {
  if (import.meta.env.PROD) {
    Sentry.addBreadcrumb({
      message,
      category,
      data,
      level,
    });
  } else {
    console.log(`[Breadcrumb] ${category}: ${message}`, data);
  }
};

// Track user context (call after auth)
export const setUserContext = (user: {
  id: string;
  email?: string;
  role?: string;
}) => {
  if (import.meta.env.PROD) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.email,
      role: user.role,
    });
  }
};

// Clear user context on logout
export const clearUserContext = () => {
  if (import.meta.env.PROD) {
    Sentry.setUser(null);
  }
};
